import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, Check, Minus, X, Search } from "lucide-react";
import {
  type PermissionGroup,
  LABEL_CONFIG,
  getChildGroups,
  getRootGroups,
  getGroupPath,
  getAncestorIds,
  toggleWithPruning,
  hasSelectedDescendant,
  hasSelectedAncestor,
} from "./types";

// --- Checkbox ---

type CheckboxState = "checked" | "indeterminate" | "covered" | "unchecked";

const TriCheckbox = ({ state }: { state: CheckboxState }) => {
  const base = "w-4 h-4 flex items-center justify-center rounded border transition-colors shrink-0";
  if (state === "checked") {
    return (
      <span className={`${base} bg-blue-500 border-blue-500 text-white`}>
        <Check size={12} />
      </span>
    );
  }
  if (state === "indeterminate") {
    return (
      <span className={`${base} bg-blue-500/40 border-blue-500 text-white`}>
        <Minus size={12} />
      </span>
    );
  }
  if (state === "covered") {
    return (
      <span className={`${base} bg-blue-500/20 border-blue-500/40 text-blue-300/70`}>
        <Check size={12} />
      </span>
    );
  }
  return <span className={`${base} bg-transparent border-zinc-600`} />;
};

// --- Tree Node ---

type TreeNodeProps = {
  group: PermissionGroup;
  groups: PermissionGroup[];
  selectedIds: ReadonlySet<string>;
  expandedIds: Set<string>;
  level: number;
  onToggle: (group: PermissionGroup) => void;
  onToggleExpand: (groupId: string) => void;
};

const TreeNode = ({ group, groups, selectedIds, expandedIds, level, onToggle, onToggleExpand }: TreeNodeProps) => {
  const children = getChildGroups(groups, group.id);
  const hasChildren = children.length > 0;
  const isExpanded = expandedIds.has(group.id);
  const isSelected = selectedIds.has(group.id);
  const isCovered = !isSelected && hasSelectedAncestor(groups, selectedIds, group.id);
  const isIndeterminate = !isSelected && hasSelectedDescendant(groups, selectedIds, group.id);
  const config = LABEL_CONFIG[group.label];
  const Icon = config.icon;

  const checkboxState: CheckboxState = isSelected
    ? "checked"
    : isIndeterminate
      ? "indeterminate"
      : isCovered
        ? "covered"
        : "unchecked";

  return (
    <div>
      <button
        type="button"
        onClick={() => onToggle(group)}
        className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-sm rounded transition-colors ${
          isSelected
            ? "bg-zinc-800 text-white"
            : isCovered
              ? "text-zinc-500 hover:bg-zinc-800/60"
              : "text-zinc-300 hover:bg-zinc-800 hover:text-white"
        }`}
        style={{ paddingLeft: level * 16 + 8 }}
      >
        {hasChildren ? (
          <span
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(group.id);
            }}
            className="p-0.5 -ml-1 text-zinc-500 hover:text-white shrink-0"
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
        ) : (
          <span className="w-5 shrink-0" />
        )}

        <TriCheckbox state={checkboxState} />

        <div className={`w-5 h-5 flex items-center justify-center rounded shrink-0 ${config.bgColor}`}>
          <Icon size={12} className={config.color} />
        </div>

        <span className="flex-1 truncate">{group.name}</span>

        {isCovered && <span className="text-[10px] uppercase tracking-wide text-zinc-600 shrink-0">via parent</span>}
      </button>

      {isExpanded && hasChildren && (
        <div>
          {children.map((child) => (
            <TreeNode
              key={child.id}
              group={child}
              groups={groups}
              selectedIds={selectedIds}
              expandedIds={expandedIds}
              level={level + 1}
              onToggle={onToggle}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// --- Dialog ---

type Props = {
  groups: PermissionGroup[];
  initialSelectedIds: string[];
  onClose: () => void;
  onConfirm: (ids: string[]) => void;
};

export const HierarchyMultiPickerDialog = ({ groups, initialSelectedIds, onClose, onConfirm }: Props) => {
  const [selected, setSelected] = useState<Set<string>>(new Set(initialSelectedIds));
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    const ids = new Set<string>();
    for (const id of initialSelectedIds) {
      for (const a of getAncestorIds(groups, id)) ids.add(a);
    }
    return ids;
  });
  const [query, setQuery] = useState("");

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const toggleExpand = (groupId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const toggleGroup = (group: PermissionGroup) => {
    setSelected((prev) => toggleWithPruning(groups, prev, group.id));
  };

  const expandAll = () => setExpandedIds(new Set(groups.map((g) => g.id)));
  const collapseAll = () => setExpandedIds(new Set());
  const clearAll = () => setSelected(new Set());

  // Filter: when query is set, expand all matches' ancestors so they're visible.
  const filteredGroupIds = (() => {
    if (!query.trim()) return null;
    const q = query.trim().toLowerCase();
    const matches = groups.filter((g) => g.name.toLowerCase().includes(q));
    const visible = new Set<string>();
    for (const m of matches) {
      visible.add(m.id);
      for (const a of getAncestorIds(groups, m.id)) visible.add(a);
    }
    return visible;
  })();

  const visibleRoots = filteredGroupIds
    ? getRootGroups(groups).filter((g) => filteredGroupIds.has(g.id))
    : getRootGroups(groups);

  // When filtering, force-expand matched ancestors so results are reachable.
  const effectiveExpanded = filteredGroupIds ? new Set([...expandedIds, ...filteredGroupIds]) : expandedIds;

  const selectedGroups = [...selected]
    .map((id) => groups.find((g) => g.id === id))
    .filter((g): g is PermissionGroup => g !== undefined);

  // Count of everything covered (selected nodes + their descendants).
  const coverageCount = (() => {
    if (selected.size === 0) return 0;
    let n = 0;
    for (const g of groups) {
      if (selected.has(g.id) || hasSelectedAncestor(groups, selected, g.id)) n++;
    }
    return n;
  })();

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl max-h-[90vh] min-h-[600px] bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div>
            <h2 className="text-base font-semibold text-white">Select targets</h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              Pick one or more groups. Selecting a parent removes its selected children.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-zinc-800">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter..."
              className="w-full pl-8 pr-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:border-blue-500 focus:outline-none"
            />
          </div>
          <button
            type="button"
            onClick={expandAll}
            className="px-2 py-1.5 text-xs text-zinc-400 hover:text-white hover:bg-zinc-800 rounded"
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={collapseAll}
            className="px-2 py-1.5 text-xs text-zinc-400 hover:text-white hover:bg-zinc-800 rounded"
          >
            Collapse
          </button>
        </div>

        {/* Tree */}
        <div className="flex-1 overflow-auto p-2 min-h-[320px]">
          {visibleRoots.length === 0 ? (
            <div className="flex items-center justify-center h-full text-sm text-zinc-500">No matches</div>
          ) : (
            visibleRoots.map((group) => (
              <TreeNode
                key={group.id}
                group={group}
                groups={groups}
                selectedIds={selected}
                expandedIds={effectiveExpanded}
                level={0}
                onToggle={toggleGroup}
                onToggleExpand={toggleExpand}
              />
            ))
          )}
        </div>

        {/* Selection summary */}
        <div className="border-t border-zinc-800 px-5 py-3 max-h-40 overflow-auto">
          {selectedGroups.length === 0 ? (
            <p className="text-xs text-zinc-500">Nothing selected.</p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-zinc-400">
                  <span className="text-white font-medium">{selectedGroups.length}</span> selected
                  <span className="text-zinc-600">
                    {" "}
                    · covers {coverageCount} group{coverageCount === 1 ? "" : "s"}
                  </span>
                </p>
                <button type="button" onClick={clearAll} className="text-xs text-zinc-500 hover:text-zinc-300">
                  Clear all
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {selectedGroups.map((g) => {
                  const config = LABEL_CONFIG[g.label];
                  const ChipIcon = config.icon;
                  const path = getGroupPath(groups, g.id)
                    .map((p) => p.name)
                    .join(" → ");
                  return (
                    <span
                      key={g.id}
                      className="inline-flex items-center gap-1.5 pl-1.5 pr-1 py-0.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-zinc-200"
                      title={path}
                    >
                      <div className={`w-4 h-4 flex items-center justify-center rounded ${config.bgColor}`}>
                        <ChipIcon size={10} className={config.color} />
                      </div>
                      <span className="truncate max-w-[180px]">{g.name}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setSelected((prev) => {
                            const next = new Set(prev);
                            next.delete(g.id);
                            return next;
                          })
                        }
                        className="p-0.5 text-zinc-400 hover:text-white"
                        aria-label={`Remove ${g.name}`}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-zinc-800">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 rounded"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm([...selected])}
            className="px-3 py-1.5 text-sm bg-blue-500 hover:bg-blue-400 text-white rounded font-medium"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default HierarchyMultiPickerDialog;
